import { useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const EnquiryModal = ({ open, onClose }) => {
  const [form, setForm] = useState({ name: "", organisation: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const handleClose = () => {
    setDone(false);
    setForm({ name: "", organisation: "", email: "", phone: "", message: "" });
    onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/enquiry`, form);
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send your inquiry. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const field = "w-full bg-white border border-[#E5E7EB] focus:border-[#041C3E] outline-none px-4 py-3 text-sm transition-colors";

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={handleClose} data-testid="enquiry-modal"
        >
          <motion.div
            className="relative w-full max-w-lg bg-white border border-[#041C3E] p-8"
            initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
            transition={{ type: "spring", damping: 26, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button data-testid="enquiry-modal-close" onClick={handleClose}
              className="absolute top-4 right-4 text-[#041C3E] hover:text-[#D94833] transition-colors" aria-label="Close">
              <X size={20} />
            </button>

            {done ? (
              <div className="text-center py-6" data-testid="enquiry-success">
                <div className="w-12 h-12 bg-[#041C3E] text-white flex items-center justify-center mx-auto"><CheckCircle2 size={26} /></div>
                <h3 className="font-head text-2xl font-black mt-5 text-[#041C3E]">Inquiry sent.</h3>
                <p className="text-[#3A4A63] mt-2 text-sm leading-relaxed">
                  Thank you. Rhea will review your requirements and get back to you shortly.
                </p>
                <button data-testid="enquiry-success-close" onClick={handleClose}
                  className="mt-6 bg-[#041C3E] text-white px-8 py-3 font-semibold tracking-wide hover:bg-[#D94833] transition-colors">
                  Close
                </button>
              </div>
            ) : (
              <>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D94833]">For institutes &amp; organizations</p>
                <h3 className="font-head text-2xl font-black mt-2 text-[#041C3E]">Inquiry for pricing and partnership</h3>
                <p className="text-sm text-[#3A4A63] mt-2">
                  Tell us about your institute, ed tech platform, skill development body or corporate team, and we will share pricing and design a program for your cohort.
                </p>
                <form onSubmit={submit} className="mt-6 space-y-4">
                  <div data-testid="enquiry-contact-fields" className="space-y-4">
                    <input data-testid="enquiry-name" required minLength={2} placeholder="Your name"
                      value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} />
                    <input data-testid="enquiry-org" placeholder="Organization"
                      value={form.organisation} onChange={(e) => setForm({ ...form, organisation: e.target.value })} className={field} />
                    <input data-testid="enquiry-email" required type="email" placeholder="Work email"
                      value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={field} />
                    <input data-testid="enquiry-phone" placeholder="Phone (optional)"
                      value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={field} />
                  </div>
                  <textarea data-testid="enquiry-message" required minLength={5} rows={4}
                    placeholder="How many learners, and what outcomes are you looking for?"
                    value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={field} />
                  <button data-testid="enquiry-submit" type="submit" disabled={busy}
                    className="w-full bg-[#D94833] text-white px-8 py-4 font-semibold tracking-wide hover:bg-[#C03F2A] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">
                    {busy ? <><Loader2 size={16} className="animate-spin" /> Sending</> : "Send inquiry"}
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};
